<template>
  <div class="home">
    <div class="header">
      <div class="search-box" ref="searchGuideTarget" :class="{ 'guide-target-active': guideVisible && guideStep === 0 }">
        <van-search
          v-model="searchValue"
          placeholder="搜索门店"
          shape="round"
          @input="onSearch"
          @clear="onSearchClear"
          clearable
        />
      </div>
    </div>

    <!-- 地图入口卡片 -->
    <div
      class="map-entry"
      ref="mapGuideTarget"
      :class="{
        'map-entry--pulse': showMapEntryPulse || (guideVisible && guideStep === 1),
        'guide-target-active': guideVisible && guideStep === 1,
      }"
      @click="openMapPanel"
    >
      <div class="map-preview">
        <van-icon name="location" color="#ee0a24" size="24" />
      </div>
      <div class="location-content">
        <div class="location-label">当前定位</div>
        <div class="location-title">
          <span>{{ locationText }}</span>
        </div>
        <div class="location-tip">定位不准？点击右侧按钮在地图上选点</div>
      </div>
      <div class="map-entry-btn">
        <van-icon name="guide-o" size="14" />
        <span>地图选点</span>
      </div>
    </div>

    <!-- 新手引导：搜索框 + 地图选点 -->
    <div v-if="guideVisible" class="guide-overlay">
      <div class="guide-click-layer" @click="nextGuideStep"></div>
      <div class="guide-hole" :style="guideHoleStyle"></div>
      <div class="guide-ring" :style="guideRingStyle"></div>
      <div class="guide-popover" :style="guidePopoverStyle">
        <div class="guide-step">{{ guideStep + 1 }}/{{ guideSteps.length }}</div>
        <div class="guide-title">{{ guideSteps[guideStep].title }}</div>
        <div class="guide-desc">{{ guideSteps[guideStep].desc }}</div>
        <div class="guide-actions">
          <button type="button" class="guide-skip" @click.stop="finishGuide">跳过</button>
          <button type="button" class="guide-next" @click.stop="nextGuideStep">
            {{ guideStep >= guideSteps.length - 1 ? '知道了' : '下一步' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 地图选点弹窗 -->
    <van-popup
      v-model="showMapPanel"
      position="bottom"
      :style="{ height: '80%' }"
      round
      closeable
    >
      <div class="map-panel">
        <div class="map-header">
          <span class="title">地图选点</span>
        </div>

        <!-- 地点搜索框 -->
        <div class="map-search">
          <van-search
            v-model="mapSearchValue"
            placeholder="搜索地点"
            shape="round"
            @input="onMapSearch"
            @clear="onMapSearchClear"
            clearable
          />
        </div>

        <!-- 搜索结果列表 -->
        <div class="search-results" v-if="mapSearchResults.length > 0">
          <div
            class="result-item"
            v-for="(item, index) in mapSearchResults"
            :key="index"
            @click="selectSearchResult(item)"
          >
            <van-icon name="location-o" color="#ee0a24" size="18" />
            <div class="result-info">
              <div class="result-title">{{ item.title }}</div>
              <div class="result-address">{{ item.address }}</div>
            </div>
          </div>
        </div>

        <!-- 地图容器 -->
        <div class="map-container" id="mapContainer" v-show="mapSearchResults.length === 0"></div>

        <!-- 当前选中位置 + 确认按钮 -->
        <div class="map-footer">
          <div class="selected-info">
            <van-icon name="location" color="#ee0a24" size="18" />
            <span class="address">{{ selectedMapLocation ? selectedMapLocation.address : '搜索或点击地图选择位置' }}</span>
          </div>
          <van-button type="primary" round block @click="confirmLocation" :disabled="!selectedMapLocation">
            确认选点
          </van-button>
        </div>
      </div>
    </van-popup>

    <!-- 店铺列表 -->
    <div class="shop-list">
      <van-list v-model="loading" :finished="true" finished-text="没有更多了">
        <div
          class="shop-item"
          :class="{ 'is-rest': shop.operationStatus !== 1 }"
          v-for="shop in shopList"
          :key="shop.shopId"
          @click="getShopMenu(shop)"
        >
          <div class="shop-card">
            <div class="shop-header">
              <div class="shop-title">
                <van-icon name="star" />
                <span class="name">{{ shop.shopName }}</span>
              </div>
              <span class="distance">{{ shop.distance }}</span>
            </div>
            <div class="shop-tags">
              <span class="tag hall">堂食</span>
              <span class="tag takeaway" v-if="shop.takeoutStatus === 1"
                >可外卖</span
              >
            </div>
            <div class="shop-info">
              <p class="address">{{ shop.shopAddress }}</p>
              <p class="business-time">
                营业时间 {{ shop.operationTime }}
                <span v-if="shop.operationStatus === 1" class="open-status">营业中</span>
                <span v-else class="rest-status">已休息</span>
              </p>
            </div>
          </div>
        </div>
      </van-list>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import { getLocation } from "@/utils/txip";

export default {
  name: "HomePage",
  async created() {
    // 获取用户定位
    await this.getUserLocation();
    // 搜索附近店铺
    this.searchShops();
  },
  data() {
    return {
      locationText: "定位中...",
      userLocation: {
        lng: 113.264359,
        lat: 23.129080,
      },
      searchValue: "",
      searchTimer: null,
      shopList: [], // 当前显示的店铺列表
      allShopList: [], // 存储所有店铺数据
      loading: false,
      // 地图相关
      showMapPanel: false,
      map: null,
      marker: null,
      selectedMapLocation: null,
      mapSearchValue: "",
      mapSearchResults: [],
      mapSearchTimer: null,
      showMapEntryPulse: true,
      // 新手引导
      guideVisible: false,
      guideStep: 0,
      guideRect: null,
      guideSteps: [
        {
          key: 'search',
          title: '搜索门店',
          desc: '在这里输入店名或关键字，快速查找附近门店',
          ref: 'searchGuideTarget',
        },
        {
          key: 'map',
          title: '地图选点',
          desc: '定位不准时，点这里打开地图选择地址，换区域找店',
          ref: 'mapGuideTarget',
        },
      ],
    };
  },
  computed: {
    guideHoleStyle() {
      const r = this.guideRect;
      if (!r) return { display: 'none' };
      const pad = 6;
      return {
        top: `${r.top - pad}px`,
        left: `${r.left - pad}px`,
        width: `${r.width + pad * 2}px`,
        height: `${r.height + pad * 2}px`,
      };
    },
    guideRingStyle() {
      return this.guideHoleStyle;
    },
    guidePopoverStyle() {
      const r = this.guideRect;
      if (!r) return { display: 'none' };
      const gap = 12;
      const popoverApproxHeight = 140;
      const spaceBelow = window.innerHeight - r.bottom;
      const placeBelow = spaceBelow > popoverApproxHeight + gap;
      if (placeBelow) {
        return {
          top: `${r.bottom + gap}px`,
          left: '16px',
          right: '16px',
        };
      }
      return {
        top: `${Math.max(12, r.top - popoverApproxHeight - gap)}px`,
        left: '16px',
        right: '16px',
      };
    },
  },
  mounted() {
    this.mapEntryPulseTimer = setTimeout(() => {
      this.showMapEntryPulse = false;
    }, 6000);
    window.addEventListener('resize', this.updateGuideLayout);
    window.addEventListener('scroll', this.updateGuideLayout, true);
  },
  watch: {
    showMapPanel(val) {
      if (val) {
        this.$nextTick(() => {
          this.initMap();
        });
      }
    },
    guideStep() {
      this.$nextTick(() => this.updateGuideLayout());
    },
  },
  beforeDestroy() {
    if (this.mapEntryPulseTimer) {
      clearTimeout(this.mapEntryPulseTimer);
    }
    window.removeEventListener('resize', this.updateGuideLayout);
    window.removeEventListener('scroll', this.updateGuideLayout, true);
    // 销毁地图实例
    if (this.map) {
      this.map.destroy();
      this.map = null;
    }
  },
  methods: {
    // 打开地图面板
    openMapPanel() {
      localStorage.setItem('mx_shop_guide_v1', '1');
      localStorage.setItem('mx_map_guide_shown', '1');
      this.showMapEntryPulse = false;
      if (this.guideVisible) this.finishGuide();
      this.showMapPanel = true;
    },

    startShopGuideIfNeeded() {
      if (localStorage.getItem('mx_shop_guide_v1')) return;
      // 兼容旧引导标记：已看过地图弹窗的用户也不再强制引导
      if (localStorage.getItem('mx_map_guide_shown')) {
        localStorage.setItem('mx_shop_guide_v1', '1');
        return;
      }
      this.guideStep = 0;
      this.guideVisible = true;
      this.$nextTick(() => {
        this.updateGuideLayout();
        // 布局稳定后再算一次
        setTimeout(() => this.updateGuideLayout(), 80);
      });
    },

    updateGuideLayout() {
      if (!this.guideVisible) return;
      const step = this.guideSteps[this.guideStep];
      const el = step && this.$refs[step.ref];
      if (!el || !el.getBoundingClientRect) {
        this.guideRect = null;
        return;
      }
      const rect = el.getBoundingClientRect();
      this.guideRect = {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom,
      };
    },

    nextGuideStep() {
      if (this.guideStep >= this.guideSteps.length - 1) {
        this.finishGuide();
        return;
      }
      this.guideStep += 1;
    },

    finishGuide() {
      this.guideVisible = false;
      this.guideRect = null;
      localStorage.setItem('mx_shop_guide_v1', '1');
      localStorage.setItem('mx_map_guide_shown', '1');
    },

    showMapGuideIfNeeded() {
      // 改由高亮引导接手，保留方法避免旧调用报错
      this.startShopGuideIfNeeded();
    },
    // 初始化腾讯地图
    initMap() {
      if (this.map) return;
      
      const TMap = window.TMap;
      if (!TMap) {
        console.error('腾讯地图 SDK 未加载');
        this.$toast('地图加载失败');
        return;
      }

      // 创建地图实例
      const center = new TMap.LatLng(this.userLocation.lat, this.userLocation.lng);
      this.map = new TMap.Map('mapContainer', {
        center: center,
        zoom: 15,
        viewMode: '2D',
      });

      // 创建标记点
      this.marker = new TMap.MultiMarker({
        map: this.map,
        styles: {
          default: new TMap.MarkerStyle({
            width: 30,
            height: 40,
            anchor: { x: 15, y: 40 },
            src: 'https://mapapi.qq.com/web/lbs/javascriptGL/demo/img/markerDefault.png',
          }),
        },
        geometries: [{
          id: 'currentLocation',
          position: center,
        }],
      });

      // 设置当前选中位置
      this.selectedMapLocation = {
        lat: this.userLocation.lat,
        lng: this.userLocation.lng,
        address: this.locationText || '当前定位位置',
      };

      // 地图点击事件 - 点击地图选择位置
      this.map.on('click', (e) => {
        const lat = e.latLng.getLat();
        const lng = e.latLng.getLng();
        this.updateMarkerPosition(lat, lng);
        
        // 直接设置选中位置（显示经纬度）
        this.selectedMapLocation = {
          lat: lat,
          lng: lng,
          address: `经度: ${lng.toFixed(4)}, 纬度: ${lat.toFixed(4)}`,
        };
      });
    },

    // 更新标记点位置
    updateMarkerPosition(lat, lng) {
      if (!this.marker) return;
      
      const TMap = window.TMap;
      this.marker.setGeometries([{
        id: 'currentLocation',
        position: new TMap.LatLng(lat, lng),
      }]);
      
      // 移动地图中心
      this.map.setCenter(new TMap.LatLng(lat, lng));
    },

    // 地点搜索（防抖）
    onMapSearch() {
      if (this.mapSearchTimer) {
        clearTimeout(this.mapSearchTimer);
      }
      this.mapSearchTimer = setTimeout(() => {
        this.searchPlaces();
      }, 300);
    },

    // 清空地点搜索
    onMapSearchClear() {
      this.mapSearchValue = '';
      this.mapSearchResults = [];
    },

    // 搜索地点（调用后端腾讯地图 suggestion 接口）
    async searchPlaces() {
      if (!this.mapSearchValue.trim()) {
        this.mapSearchResults = [];
        return;
      }

      try {
        const response = await request.post('/mx/search-location', {
          keyword: this.mapSearchValue,
        });

        if (response && response.success && response.data && response.data.data) {
          // suggestion 接口返回的数据结构
          this.mapSearchResults = response.data.data.map(item => ({
            title: item.title,
            address: item.address,
            lat: item.location.lat,
            lng: item.location.lng,
          }));
        } else {
          this.mapSearchResults = [];
        }
      } catch (error) {
        console.error('搜索地点失败:', error);
        this.mapSearchResults = [];
      }
    },

    // 选择搜索结果
    selectSearchResult(item) {
      this.selectedMapLocation = {
        lat: item.lat,
        lng: item.lng,
        address: item.title,
      };
      
      // 更新地图位置
      this.updateMarkerPosition(item.lat, item.lng);
      
      // 清空搜索结果，显示地图
      this.mapSearchResults = [];
      this.mapSearchValue = item.title;
    },

    // 确认选择位置
    confirmLocation() {
      if (!this.selectedMapLocation) {
        this.$toast('请搜索或点击地图选择位置');
        return;
      }

      // 更新用户位置
      this.userLocation = {
        lat: this.selectedMapLocation.lat,
        lng: this.selectedMapLocation.lng,
      };
      this.locationText = this.selectedMapLocation.address || '已选位置';
      
      // 关闭地图面板
      this.showMapPanel = false;
      
      // 清空搜索
      this.mapSearchValue = '';
      this.mapSearchResults = [];
      
      // 重新搜索店铺
      this.searchShops();
    },

    // 店铺名称搜索（防抖）
    onSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer);
      }
      this.searchTimer = setTimeout(() => {
        this.filterShops();
      }, 300);
    },

    // 前端过滤店铺
    filterShops() {
      if (!this.searchValue.trim()) {
        this.shopList = this.allShopList;
        return;
      }

      const keyword = this.searchValue.toLowerCase().trim();
      this.shopList = this.allShopList.filter((shop) => {
        const nameMatch = shop.shopName && shop.shopName.toLowerCase().includes(keyword);
        const addressMatch = shop.shopAddress && shop.shopAddress.toLowerCase().includes(keyword);
        return nameMatch || addressMatch;
      });
    },

    // 清空搜索
    onSearchClear() {
      this.searchValue = "";
      this.shopList = this.allShopList;
    },

    async getUserLocation() {
      try {
        this.locationText = "定位中...";
        
        // 使用腾讯定位服务（优先浏览器定位，失败则用 IP 定位）
        const location = await getLocation();
        
        this.userLocation = {
          lng: location.lng,
          lat: location.lat,
        };
        
        // 根据定位类型显示不同文本
        if (location.type === 'browser') {
          this.locationText = "精确定位";
        } else if (location.type === 'ip') {
          this.locationText = location.city || "IP定位";
        } else {
          this.locationText = "默认位置";
        }
        
        console.log('定位成功:', location);
        this.showMapGuideIfNeeded();
      } catch (error) {
        console.error("获取定位失败：", error);
        this.locationText = "定位失败";
        
        // 使用默认位置（广州）
        this.userLocation = {
          lng: 113.264359,
          lat: 23.129080,
        };
      }
    },
    async searchShops() {
      try {
        this.loading = true;
        const config = this.$store.state.appConfig;
        if (!config) {
          console.error("应用配置尚未加载，无法搜索店铺");
          this.loading = false;
          return;
        }

        // 检查是否有 couponRuleId
        if (!config.dataId) {
          console.error("缺少 couponRuleId，无法搜索店铺");
          this.$toast("缺少优惠券信息");
          this.loading = false;
          return;
        }

        // 蜜雪冰城接口参数
        const params = {
          longitude: this.userLocation.lng.toString(),
          latitude: this.userLocation.lat.toString(),
          couponRuleId: config.dataId,
          distance: 20,
          accessToken: this.$store.state.mxAccessToken,
        };
        
        console.log("搜索店铺参数：", params);
        
        // 调用蜜雪冰城店铺接口
        let response = await request.post("/mx/find-near-shops", params);
        console.log("response", response);

        console.log("店铺接口返回数据：", response);
        if (response.code == 0) {
          this.allShopList = response.data || []; // 存储所有店铺数据
          this.shopList = this.allShopList; // 初始显示所有店铺
          console.log("获取到的店铺列表：", this.allShopList);
        } else {
          console.error("获取店铺列表失败：", response.msg);
          this.$toast(response.msg || "获取店铺列表失败");
        }
      } catch (error) {
        console.error("搜索店铺出错：", error);
        this.$toast("搜索店铺失败");
      } finally {
        this.loading = false;
      }
    },
    formatBusinessTime(time) {
      if (!time) return "";
      // 直接返回已格式化的时间字符串
      return time;
    },
    getShopMenu(shop) {
      if (shop.operationStatus !== 1) {
        this.$toast('该门店已休息，请选择其他门店');
        return;
      }

      // 直接跳转到商品列表页，使用 query 传递店铺基本信息
      this.$router.push({
        path: "/goods-list",
        query: {
          shopId: shop.shopId,
          shopName: shop.shopName,
          shopAddress: shop.shopAddress,
          shopTel: shop.contactPhone,
          shopLongitude: shop.longitude,
          shopLatitude: shop.latitude,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.header {
  padding: 8px 12px;
  background: #fff;

  .search-box {
    :deep(.van-search) {
      padding: 0;
    }

    :deep(.van-search__content) {
      background: #f7f8fa;
    }
  }
}

// 地图入口卡片
.map-entry {
  display: flex;
  align-items: center;
  margin: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ffd6d9;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(238, 10, 36, 0.08);
  cursor: pointer;

  &:active {
    background: #fff8f8;
  }

  &--pulse .map-entry-btn {
    animation: map-btn-pulse 1.2s ease-in-out infinite;
  }

  .map-preview {
    width: 44px;
    height: 44px;
    background: #fff2f3;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 12px;
    flex-shrink: 0;
  }

  .location-content {
    flex: 1;
    min-width: 0;

    .location-label {
      font-size: 11px;
      color: #969799;
      margin-bottom: 2px;
    }

    .location-title {
      margin-bottom: 4px;

      span {
        font-size: 16px;
        font-weight: 600;
        color: #323233;
      }
    }

    .location-tip {
      font-size: 12px;
      color: #646566;
      line-height: 1.4;
    }
  }

  .map-entry-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 64px;
    padding: 8px 10px;
    margin-left: 10px;
    background: linear-gradient(135deg, #ff6034, #ee0a24);
    border-radius: 8px;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(238, 10, 36, 0.25);

    .van-icon {
      color: #fff;
    }
  }
}

@keyframes map-btn-pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 2px 6px rgba(238, 10, 36, 0.25);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(238, 10, 36, 0.4);
  }
}

.shop-list {
  padding: 12px;
  // 移除背景色，让 body 的背景色透出来
  // background: #f7f8fa;
  min-height: calc(100vh - 54px);

  .shop-item {
    margin-bottom: 12px;

    &.is-rest {
      .shop-card {
        opacity: 0.6;
        cursor: not-allowed;

        &:active {
          opacity: 0.6;
        }
      }
    }
  }

  .shop-card {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    cursor: pointer;
    transition: all 0.2s;
    text-align: left;

    &:active {
      opacity: 0.8;
    }

    .shop-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;

      .shop-title {
        display: flex;
        align-items: center;
        font-size: 16px;
        font-weight: 500;
        flex: 1;
        min-width: 0; // 保留这个关键的 flex 修正

        .van-icon {
          color: #ffd21e;
          margin-right: 4px;
        }

        .name {
          color: #323233;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      .distance {
        color: #969799;
        font-size: 14px;
        margin-left: 8px;
        white-space: nowrap;
      }
    }

    .shop-tags {
      margin-bottom: 8px;

      .tag {
        display: inline-block;
        padding: 2px 6px;
        margin-right: 8px;
        border-radius: 4px;
        font-size: 12px;
      }

      .hall {
        color: #07c160;
        background: #e8f7ef;
      }

      .takeaway {
        color: #1989fa;
        background: #eaf3fe;
      }
    }

    .shop-info {
      font-size: 13px;
      color: #646566;
      text-align: left;

      .address {
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .business-time {
        margin: 0;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .rest-status {
        color: #969799;
        background: #f2f3f5;
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 11px;
      }

      .open-status {
        color: #07c160;
        background: #e8f7ef;
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 11px;
      }
    }
  }
}

// 地图面板样式
.map-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;

  .map-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid #f5f5f5;

    .title {
      font-size: 16px;
      font-weight: 500;
      color: #323233;
    }
  }

  .map-search {
    padding: 8px 12px;
    background: #fff;

    :deep(.van-search) {
      padding: 0;
    }

    :deep(.van-search__content) {
      background: #f7f8fa;
    }
  }

  .search-results {
    flex: 1;
    overflow-y: auto;
    background: #fff;

    .result-item {
      display: flex;
      align-items: flex-start;
      padding: 14px 16px;
      border-bottom: 1px solid #f5f5f5;
      cursor: pointer;

      &:active {
        background: #f7f8fa;
      }

      .van-icon {
        margin-right: 12px;
        margin-top: 2px;
        flex-shrink: 0;
      }

      .result-info {
        flex: 1;
        min-width: 0;

        .result-title {
          font-size: 15px;
          color: #323233;
          margin-bottom: 4px;
        }

        .result-address {
          font-size: 12px;
          color: #969799;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .map-container {
    flex: 1;
    min-height: 200px;
  }

  .map-footer {
    padding: 12px 16px;
    background: #fff;
    border-top: 1px solid #f5f5f5;

    .selected-info {
      display: flex;
      align-items: center;
      margin-bottom: 12px;

      .van-icon {
        margin-right: 8px;
        flex-shrink: 0;
      }

      .address {
        font-size: 14px;
        color: #323233;
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .van-button {
      height: 44px;
      font-size: 16px;
    }
  }
}

// 新手引导
.guide-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  pointer-events: none;
}

.guide-click-layer {
  position: absolute;
  inset: 0;
  pointer-events: auto;
}

.guide-hole {
  position: fixed;
  border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.55);
  background: transparent;
  pointer-events: none;
  z-index: 3001;
}

.guide-ring {
  position: fixed;
  border-radius: 12px;
  border: 2px solid #ee0a24;
  box-shadow: 0 0 0 4px rgba(238, 10, 36, 0.25);
  pointer-events: none;
  z-index: 3002;
  animation: guide-ring-pulse 1.2s ease-in-out infinite;
}

.guide-popover {
  position: fixed;
  z-index: 3003;
  pointer-events: auto;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.guide-step {
  font-size: 12px;
  color: #ee0a24;
  font-weight: 600;
  margin-bottom: 6px;
}

.guide-title {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  margin-bottom: 6px;
}

.guide-desc {
  font-size: 13px;
  color: #646566;
  line-height: 1.5;
  margin-bottom: 14px;
}

.guide-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.guide-skip {
  border: none;
  background: transparent;
  color: #969799;
  font-size: 14px;
  padding: 6px 0;
}

.guide-next {
  border: none;
  background: #ee0a24;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 18px;
  border-radius: 18px;
}

.guide-target-active {
  position: relative;
  z-index: 3002;
}

@keyframes guide-ring-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(238, 10, 36, 0.2);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(238, 10, 36, 0.12);
  }
}
</style> 